import { useEffect, useRef } from "react";
import { captureLead } from "@/lib/leads.functions";

const SCRIPT_SRC = "https://js-na2.hsforms.net/forms/embed/40934946.js";
const PORTAL_ID = "40934946";
const FORM_ID = "8ad22e3e-5909-411f-bf6d-783a3336bbf6";

type HsForm = { getFormFieldValues: () => Promise<{ name: string; value: unknown }[]> };
declare global {
  interface Window {
    HubSpotFormsV4?: { getFormFromEvent: (e: Event) => HsForm | undefined };
    __plLeadListener?: boolean;
  }
}

/** One global listener: saves every successful HubSpot submission to our leads table. */
function installLeadListener() {
  if (window.__plLeadListener) return;
  window.__plLeadListener = true;
  window.addEventListener("hs-form-event:on-submission:success", async (event) => {
    try {
      const form = window.HubSpotFormsV4?.getFormFromEvent(event);
      if (!form) return;
      const values = await form.getFormFieldValues();
      const fields: Record<string, string> = {};
      for (const { name, value } of values) {
        if (value === undefined || value === null || value === "") continue;
        const key = name.includes("/") ? name.split("/").pop() : name;
        if (!key) continue;
        fields[key] = Array.isArray(value) ? value.join(", ") : String(value);
      }
      await captureLead({ data: { fields, page_url: window.location.href, form_id: FORM_ID } });
    } catch (err) {
      console.error("[leads] capture failed", err);
    }
  });
}

/**
 * Embeds the HubSpot form. The HubSpot embed script scans the DOM for
 * `.hs-form-frame` when it loads, so on client-side route changes we re-append
 * the script to force it to pick up the newly mounted container.
 */
export function HubSpotForm({ className, lazy = false, locationAudit = false }: { className?: string; lazy?: boolean; locationAudit?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const load = () => {
      installLeadListener();
      const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`);
      if (locationAudit && existing) return;
      if (existing) existing.remove();
      const script = document.createElement("script");
      script.src = SCRIPT_SRC;
      script.defer = true;
      document.body.appendChild(script);
    };
    if (!lazy || !('IntersectionObserver' in window)) {
      load();
      return;
    }
    const container = ref.current;
    if (!container) return;
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      load();
    }, { rootMargin: locationAudit ? "600px" : "400px" });
    observer.observe(container);
    return () => observer.disconnect();
  }, [lazy, locationAudit]);


  return (
    <div className={`hubspot-form min-w-0 max-w-full ${locationAudit ? "min-h-[800px]" : lazy ? "h-[800px] overflow-y-auto" : "overflow-hidden"} ${className ?? ""}`}>
      <div
        ref={ref}
        className="hs-form-frame"
        data-region="na2"
        data-form-id={FORM_ID}
        data-portal-id={PORTAL_ID}
      />
    </div>
  );
}
